import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  Plus, Receipt, Search, CheckCircle, Clock, AlertTriangle, Send, XCircle,
  Loader2, Euro, Calendar, User, Printer, Trash2, X, Edit2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-500', icon: Clock },
  sent: { label: 'Envoyée', color: 'bg-blue-500', icon: Send },
  paid: { label: 'Payée', color: 'bg-green-500', icon: CheckCircle },
  unpaid: { label: 'Impayée', color: 'bg-red-500', icon: XCircle },
  overdue: { label: 'En retard', color: 'bg-red-600', icon: AlertTriangle },
};

const CRMInvoices = () => {
  const { token } = useAuth();
  const { darkMode } = useTheme();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    clientId: '',
    title: '',
    items: [{ description: '', quantity: 1, unitPrice: 0, total: 0 }],
    notes: '',
    dueDate: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, clientsRes] = await Promise.all([
        fetch(`${API_URL}/api/crm/invoices`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/crm/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (invoicesRes.ok) setInvoices(await invoicesRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/crm/invoices/standalone`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null
        })
      });

      if (response.ok) {
        fetchData();
        closeModal();
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await fetch(`${API_URL}/api/crm/invoices/${id}/status?status=${status}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) return;

    try {
      await fetch(`${API_URL}/api/crm/invoices/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting invoice:', error);
    }
  };

  const openModal = () => {
    setFormData({
      clientId: clients[0]?.id || '',
      title: '',
      items: [{ description: '', quantity: 1, unitPrice: 0, total: 0 }],
      notes: '',
      dueDate: ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'unitPrice') {
      newItems[index].total = newItems[index].quantity * newItems[index].unitPrice;
    }
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unitPrice: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== index)
      });
    }
  };

  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
    const tax = subtotal * 0.20;
    return { subtotal, tax, total: subtotal + tax };
  };

  const printInvoice = (invoice) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Facture ${invoice.invoiceNumber}</title>
        <style>
          body { font-family: 'Segoe UI', sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
          .logo { font-size: 24px; font-weight: bold; color: #f59e0b; }
          .invoice-info { text-align: right; }
          .client-info { margin-bottom: 30px; padding: 20px; background: #f3f4f6; border-radius: 8px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          th { background: #f3f4f6; }
          .totals { margin-top: 20px; text-align: right; }
          .totals div { padding: 8px 0; }
          .total-final { font-size: 20px; font-weight: bold; color: #f59e0b; border-top: 2px solid #e5e7eb; padding-top: 12px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo">☀️ Soleil d'Azur</div>
            <p>Agence de Communication</p>
          </div>
          <div class="invoice-info">
            <h2>FACTURE</h2>
            <p><strong>${invoice.invoiceNumber}</strong></p>
            <p>Date: ${new Date(invoice.createdAt).toLocaleDateString('fr-FR')}</p>
            ${invoice.dueDate ? `<p>Échéance: ${new Date(invoice.dueDate).toLocaleDateString('fr-FR')}</p>` : ''}
          </div>
        </div>
        
        <div class="client-info">
          <strong>Client:</strong><br>
          ${invoice.clientName}
        </div>
        
        <h3>${invoice.title}</h3>
        
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Quantité</th>
              <th>Prix unitaire</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${invoice.items.map(item => `
              <tr>
                <td>${item.description}</td>
                <td>${item.quantity}</td>
                <td>${item.unitPrice.toFixed(2)} €</td>
                <td>${item.total.toFixed(2)} €</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <div>Sous-total HT: ${invoice.subtotal.toFixed(2)} €</div>
          <div>TVA (20%): ${invoice.tax.toFixed(2)} €</div>
          <div class="total-final">Total TTC: ${invoice.total.toFixed(2)} €</div>
        </div>
        
        ${invoice.notes ? `<p><strong>Notes:</strong> ${invoice.notes}</p>` : ''}
        
        <div class="footer">
          <p>Soleil d'Azur - Agence de Communication</p>
          <p>Merci pour votre confiance !</p>
        </div>
        
        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const filteredInvoices = invoices.filter(invoice => {
    const search = searchTerm.toLowerCase();
    return (
      invoice.title.toLowerCase().includes(search) ||
      invoice.clientName.toLowerCase().includes(search) ||
      invoice.invoiceNumber.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className={`text-3xl font-inter font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Factures
          </h1>
          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {invoices.length} facture{invoices.length > 1 ? 's' : ''} au total
          </p>
        </div>
        <Button 
          onClick={openModal}
          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-full"
          disabled={clients.length === 0}
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouvelle facture
        </Button>
      </div>

      {clients.length === 0 && (
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-700'}`}>
          ⚠️ Créez d'abord un client avant de créer une facture
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        <Input
          placeholder="Rechercher une facture..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`pl-10 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : ''}`}
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>En attente</p>
                <p className="text-2xl font-bold text-amber-500">
                  {formatCurrency(invoices.filter(i => i.status === 'pending' || i.status === 'sent').reduce((sum, i) => sum + i.total, 0))}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Payées</p>
                <p className="text-2xl font-bold text-green-500">
                  {formatCurrency(invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.total, 0))}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Impayées</p>
                <p className="text-2xl font-bold text-red-500">
                  {formatCurrency(invoices.filter(i => i.status === 'unpaid').reduce((sum, i) => sum + i.total, 0))}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(invoices.reduce((sum, i) => sum + i.total, 0))}
                </p>
              </div>
              <Euro className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoices List */}
      <div className="space-y-4">
        {filteredInvoices.map(invoice => {
          const StatusIcon = STATUS_CONFIG[invoice.status]?.icon || Receipt;
          return (
            <Card key={invoice.id} className={`${darkMode ? 'bg-gray-800 border-gray-700' : ''}`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${STATUS_CONFIG[invoice.status]?.color || 'bg-gray-500'}`}>
                      <StatusIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {invoice.invoiceNumber}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs text-white ${STATUS_CONFIG[invoice.status]?.color}`}>
                          {STATUS_CONFIG[invoice.status]?.label}
                        </span>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {invoice.title}
                      </p>
                      <div className={`flex items-center gap-4 mt-2 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {invoice.clientName}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(invoice.createdAt).toLocaleDateString('fr-FR')}
                        </span>
                        {invoice.paidAt && (
                          <span className="flex items-center gap-1 text-green-500">
                            <CheckCircle className="w-4 h-4" />
                            Payée le {new Date(invoice.paidAt).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col md:items-end gap-3">
                    <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(invoice.total)}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {invoice.status === 'pending' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(invoice.id, 'sent')}>
                          <Send className="w-4 h-4 mr-1" /> Envoyer
                        </Button>
                      )}
                      {(invoice.status === 'pending' || invoice.status === 'sent') && (
                        <>
                          <Button size="sm" onClick={() => updateStatus(invoice.id, 'paid')} className="bg-green-500 hover:bg-green-600 text-white">
                            <CheckCircle className="w-4 h-4 mr-1" /> Payée
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => updateStatus(invoice.id, 'unpaid')} className="text-red-500 border-red-500">
                            <XCircle className="w-4 h-4 mr-1" /> Impayée
                          </Button>
                        </>
                      )}
                      {invoice.status === 'unpaid' && (
                        <Button size="sm" onClick={() => updateStatus(invoice.id, 'paid')} className="bg-green-500 hover:bg-green-600 text-white">
                          <CheckCircle className="w-4 h-4 mr-1" /> Marquer payée
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => printInvoice(invoice)}>
                        <Printer className="w-4 h-4 mr-1" /> Imprimer
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(invoice.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredInvoices.length === 0 && (
        <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Aucune facture trouvée</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className={`w-full max-w-3xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}>
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-inherit z-10 border-b">
              <CardTitle className={darkMode ? 'text-white' : ''}>
                Nouvelle facture
              </CardTitle>
              <button onClick={closeModal} className="p-2">
                <X className="w-5 h-5" />
              </button>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Client Selection */}
                <div>
                  <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Client *
                  </label>
                  <select
                    value={formData.clientId}
                    onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                    required
                    className={`w-full mt-1 rounded-md border px-3 py-2 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="">Sélectionner un client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.firstName} {client.lastName} {client.company && `(${client.company})`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Title */}
                <div>
                  <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Titre de la facture *
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Création site web"
                    required
                    className={darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}
                  />
                </div>

                {/* Due Date */}
                <div>
                  <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Date d'échéance
                  </label>
                  <Input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className={darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}
                  />
                </div>

                {/* Items */}
                <div>
                  <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Prestations
                  </label>
                  <div className="space-y-3 mt-2">
                    {formData.items.map((item, index) => (
                      <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div className="grid grid-cols-12 gap-3">
                          <div className="col-span-12 md:col-span-5">
                            <Input
                              placeholder="Description"
                              value={item.description}
                              onChange={(e) => updateItem(index, 'description', e.target.value)}
                              className={darkMode ? 'bg-gray-600 border-gray-500 text-white' : ''}
                            />
                          </div>
                          <div className="col-span-4 md:col-span-2">
                            <Input
                              type="number"
                              placeholder="Qté"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                              className={darkMode ? 'bg-gray-600 border-gray-500 text-white' : ''}
                            />
                          </div>
                          <div className="col-span-4 md:col-span-2">
                            <Input
                              type="number"
                              placeholder="Prix unit."
                              min="0"
                              step="0.01"
                              value={item.unitPrice}
                              onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                              className={darkMode ? 'bg-gray-600 border-gray-500 text-white' : ''}
                            />
                          </div>
                          <div className="col-span-3 md:col-span-2 flex items-center">
                            <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>
                              {formatCurrency(item.total)}
                            </span>
                          </div>
                          <div className="col-span-1 flex items-center justify-end">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="outline" onClick={addItem} className="mt-3">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter une ligne
                  </Button>
                </div>

                {/* Totals */}
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Sous-total HT</span>
                      <span className={darkMode ? 'text-white' : ''}>{formatCurrency(calculateTotal().subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>TVA (20%)</span>
                      <span className={darkMode ? 'text-white' : ''}>{formatCurrency(calculateTotal().tax)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span className={darkMode ? 'text-white' : ''}>Total TTC</span>
                      <span className="text-amber-500">{formatCurrency(calculateTotal().total)}</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    placeholder="Conditions de paiement, etc."
                    className={`w-full mt-1 rounded-md border px-3 py-2 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={closeModal}>
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={saving}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                  >
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Créer la facture'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CRMInvoices;
